import type { DataPoint } from "@/types/database-adapter";
import { apiConfig } from "@/config";

export interface SampleDataset {
  id: string;
  symbol: string; // e.g., BTC_USD
  displayName: string;
  description: string;
  timeframe: string; // e.g., D1
  url: string; // static dev path
}

export const SAMPLE_DATASETS: SampleDataset[] = [
  {
    id: "btc-daily",
    symbol: "BTC_USD",
    displayName: "Bitcoin (BTC/USD)",
    description: "Daily timeframe",
    timeframe: "D1",
    url: "/data/BTC_USD_D1_20140917_20250622.json",
  },
  {
    id: "eth-daily",
    symbol: "ETH_USD",
    displayName: "Ethereum (ETH/USD)",
    description: "Daily timeframe",
    timeframe: "D1",
    url: "/data/ETH_USD_D1_20171109_20250622.json",
  },
  {
    id: "sol-daily",
    symbol: "SOL_USD",
    displayName: "Solana (SOL/USD)",
    description: "Daily timeframe",
    timeframe: "D1",
    url: "/data/SOL_USD_D1_20200410_20250622.json",
  },
  {
    id: "xrp-daily",
    symbol: "XRP_USD",
    displayName: "Ripple (XRP/USD)",
    description: "Daily timeframe",
    timeframe: "D1",
    url: "/data/XRP_USD_D1_20171109_20250622.json",
  },
];

export async function loadSampleData(datasetId: string): Promise<{ data: DataPoint[]; meta: { symbol: string; timeframe: string; start?: string; end?: string; source?: string } }> {
  const dataset = SAMPLE_DATASETS.find((d) => d.id === datasetId);
  if (!dataset) throw new Error(`Sample dataset ${datasetId} not found`);

  // Try static path first (local/dev)
  try {
    const resp = await fetch(dataset.url);
    if (resp.ok) {
      const json = (await resp.json()) as unknown;
      if (Array.isArray(json)) {
        return { data: json as DataPoint[], meta: { symbol: dataset.symbol, timeframe: dataset.timeframe } };
      }
    }
  } catch (error) {
    // Log static path failure and fallback to API
    console.warn(`Failed to load static sample data from ${dataset.url}, falling back to API:`, error);
  }

  // Fallback to backend API
  try {
    const params = new URLSearchParams({ symbol: dataset.symbol, timeframe: dataset.timeframe });
    const apiUrl = `${apiConfig.baseUrl}/sample-data?${params.toString()}`;
    const apiResp = await fetch(apiUrl);
    if (!apiResp.ok) throw new Error(`Failed to load sample data: ${apiResp.statusText}`);
    const payload = (await apiResp.json()) as { data: DataPoint[]; symbol: string; timeframe: string; start?: string; end?: string; source?: string };
    return { data: payload.data, meta: { symbol: payload.symbol, timeframe: payload.timeframe, start: payload.start, end: payload.end, source: payload.source } };
  } catch (error) {
    console.error('Failed to load sample data from API:', error);
    throw new Error(`Unable to load sample data for ${dataset.displayName}: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

